import * as React from 'react';

import { formatTime } from '@/lib/utils/date';
import { useActiveActivity } from '@/features/activities/hooks/useActivities';
import type { TaskActivity } from '../types';

interface ActivityRunTimeProps {
  activity: TaskActivity;
}

export function ActivityRunTime({ activity }: ActivityRunTimeProps) {
  const [displayTime, setDisplayTime] = React.useState(0);
  const { data: activeActivity } = useActiveActivity();

  // Use active activity data if this is the currently running activity (keeps in sync with navbar)
  const isActive = activeActivity && activeActivity.id === activity.id;
  const source = isActive ? activeActivity : activity;
  const isRunning = source.lastStartTime && !source.lastEndTime;

  React.useEffect(() => {
    // If activity is stopped, show userEnteredTimeInSeconds
    if (!isRunning) {
      setDisplayTime(source.userEnteredTimeInSeconds || 0);
      return;
    }

    // Activity is running - calculate and auto-increment
    const base = Number(source.runTimeInSeconds || 0);
    const serverStart = new Date(source.lastStartTime!).getTime();
    const serverNow = isActive && activeActivity.currentTime
      ? new Date(activeActivity.currentTime).getTime()
      : Date.now();
    const elapsedOnServer = Math.max(0, Math.floor((serverNow - serverStart) / 1000));
    const initialRun = base + elapsedOnServer;
    const fetchPerf = performance.now();

    setDisplayTime(initialRun);

    const interval = setInterval(() => {
      const elapsedSinceFetch = Math.floor((performance.now() - fetchPerf) / 1000);
      setDisplayTime(() => initialRun + elapsedSinceFetch);
    }, 1000);

    return () => clearInterval(interval);
  }, [source, isRunning, isActive, activeActivity]);

  return <span className="font-mono text-sm text-muted-foreground">{formatTime(displayTime)}</span>;
}
